(() => {
  if (customElements.get('fl-sticky-purchase-bar')) return;

  class FLStickyPurchaseBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productRoot = document.getElementById(`MainProduct-${this.sectionId}`);
      this.productInfo = document.getElementById(`ProductInfo-${this.sectionId}`);
      this.proxyButton = this.querySelector('[data-fl-sticky-submit]');
      this.variantOutput = this.querySelector('[data-fl-sticky-variant]');
      this.priceOutput = this.querySelector('[data-fl-sticky-price]');
      this.quantityOutput = this.querySelector('[data-fl-sticky-quantity]');
      this.labelOutput = this.querySelector('[data-fl-sticky-label]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.canonicalButtonVisible = false;
      this.syncQueued = false;

      if (!this.productRoot || !this.productInfo || !this.proxyButton) return;

      this.handleProxyClick = () => {
        this.refreshReferences();
        if (!this.canonicalButton || this.canonicalButton.disabled) return;
        this.canonicalButton.click();
      };
      this.handleQuantityChange = () => this.queueSync();
      this.handleViewportChange = () => this.updateVisibility();
      this.proxyButton.addEventListener('click', this.handleProxyClick);
      window.addEventListener('resize', this.handleViewportChange, { passive: true });

      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.handleViewportChange);
      } else {
        this.mobileQuery.addListener(this.handleViewportChange);
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const current = entries.find((entry) => entry.target === this.canonicalButton);
          if (!current) return;
          this.canonicalButtonVisible = current.isIntersecting;
          this.updateVisibility();
        },
        { threshold: 0.15 }
      );

      this.productObserver = new MutationObserver(() => this.queueSync());
      this.productObserver.observe(this.productInfo, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-disabled', 'class', 'disabled', 'value']
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const eventSectionId = event?.data?.sectionId;
          if (eventSectionId && String(eventSectionId) !== String(this.sectionId)) return;
          this.queueSync();
        });
      }

      this.refreshReferences();
      if (!this.canonicalButton) return;

      this.hidden = false;
      document.body.classList.add('fl-sticky-purchase-ready');
      this.sync();
    }

    disconnectedCallback() {
      this.proxyButton?.removeEventListener('click', this.handleProxyClick);
      this.quantityInput?.removeEventListener('input', this.handleQuantityChange);
      this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.intersectionObserver?.disconnect();
      this.productObserver?.disconnect();
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();

      if (typeof this.mobileQuery?.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.handleViewportChange);
      } else {
        this.mobileQuery?.removeListener(this.handleViewportChange);
      }

      document.body.classList.remove('fl-sticky-purchase-ready', 'fl-sticky-purchase-active');
    }

    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      window.requestAnimationFrame(() => {
        this.syncQueued = false;
        this.sync();
      });
    }

    refreshReferences() {
      const nextButton = this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}[name="add"]`);
      const nextQuantity = this.productInfo.querySelector(`#Quantity-${this.sectionId}`);

      if (nextButton !== this.canonicalButton) {
        this.intersectionObserver?.disconnect();
        this.canonicalButton = nextButton;
        if (this.canonicalButton) {
          const rect = this.canonicalButton.getBoundingClientRect();
          this.canonicalButtonVisible = rect.bottom > 0 && rect.top < window.innerHeight;
          this.intersectionObserver?.observe(this.canonicalButton);
        } else {
          this.canonicalButtonVisible = false;
        }
      }

      if (nextQuantity !== this.quantityInput) {
        this.quantityInput?.removeEventListener('input', this.handleQuantityChange);
        this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
        this.quantityInput = nextQuantity;
        this.quantityInput?.addEventListener('input', this.handleQuantityChange);
        this.quantityInput?.addEventListener('change', this.handleQuantityChange);
      }

      this.variantInput = this.productInfo.querySelector(
        `#product-form-${this.sectionId} .product-variant-id`
      );
      this.priceRoot = this.productInfo.querySelector(`#price-${this.sectionId}`);
    }

    selectedVariantLabel() {
      const selectedControls = this.productInfo.querySelectorAll(
        'variant-selects select option:checked, variant-selects input[type="radio"]:checked'
      );
      const labels = Array.from(selectedControls)
        .map((control) => control.value || control.textContent)
        .map((value) => String(value || '').trim())
        .filter(Boolean);

      if (labels.length) return labels.join(' / ');

      const variantId = this.variantInput?.value;
      const variantOption = variantId
        ? this.productInfo.querySelector(`select[name="id"] option[value="${CSS.escape(variantId)}"]`)
        : null;
      const fallback = variantOption?.textContent?.trim();
      return fallback && fallback !== 'Default Title' ? fallback : this.variantOutput?.textContent?.trim();
    }

    selectedPrice() {
      if (!this.priceRoot) return '';
      const price = this.priceRoot.querySelector('.price');
      const selector = price?.classList.contains('price--on-sale')
        ? '.price__sale .price-item--sale'
        : '.price__regular .price-item--regular';
      const node = this.priceRoot.querySelector(selector) || this.priceRoot.querySelector('.price-item');
      return node?.textContent?.replace(/\s+/g, ' ').trim() || '';
    }

    sync() {
      this.refreshReferences();
      if (!this.canonicalButton) {
        this.hidden = true;
        this.classList.remove('is-active');
        document.body.classList.remove('fl-sticky-purchase-active');
        return;
      }

      this.hidden = false;
      document.body.classList.add('fl-sticky-purchase-ready');

      const variantLabel = this.selectedVariantLabel();
      const price = this.selectedPrice();
      const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value || '1', 10) || 1);
      const canonicalLabel =
        this.canonicalButton.querySelector(':scope > span')?.textContent?.replace(/\s+/g, ' ').trim() ||
        this.canonicalButton.textContent?.replace(/\s+/g, ' ').trim() ||
        'Add to cart';
      const busy =
        this.canonicalButton.classList.contains('loading') ||
        this.canonicalButton.getAttribute('aria-disabled') === 'true';

      if (variantLabel) this.variantOutput.textContent = variantLabel;
      if (price) this.priceOutput.textContent = price;
      this.quantityOutput.textContent = String(quantity);
      this.labelOutput.textContent = canonicalLabel;
      this.proxyButton.disabled = this.canonicalButton.disabled || busy;
      this.proxyButton.toggleAttribute('aria-busy', busy);
      this.updateVisibility();
    }

    updateVisibility() {
      const active =
        this.mobileQuery.matches &&
        Boolean(this.canonicalButton) &&
        !this.canonicalButtonVisible;
      this.classList.toggle('is-active', active);
      document.body.classList.toggle('fl-sticky-purchase-active', active);
    }
  }

  customElements.define('fl-sticky-purchase-bar', FLStickyPurchaseBar);
})();
